import type {PtSupplier, PtTermMapping} from "~/experiments/pages/payment-terms/PaymentTermsClasses.ts";

export function formatCompactCurrency(value: number, currency: string): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            notation: 'compact',
            maximumFractionDigits: value >= 1_000_000 ? 2 : 0,
        }).format(value);
    } catch {
        return `${currency} ${value.toLocaleString('en-US')}`;
    }
}

export function formatFullCurrency(value: number, currency: string): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(value);
    } catch {
        return `${currency} ${value.toLocaleString('en-US')}`;
    }
}

export function formatPct(value: number, digits = 1): string {
    return `${(value * 100).toFixed(digits)}%`;
}

// ---------------------------------------------------------------------------
// Actionability tiers (replaces the generic High/Medium/Low priority).
// Procurement prioritises immediate terms first — easiest to change, highest
// impact — then by how far below the policy target a supplier sits.
// ---------------------------------------------------------------------------

export type PtTier = 'immediate' | 'gap50' | 'gap25' | 'gapSmall' | 'atTarget';

export const TIER_LABEL: Record<PtTier, string> = {
    immediate: 'Immediate terms',
    gap50: '50%+ below target',
    gap25: '25–50% below target',
    gapSmall: '<25% below target',
    atTarget: 'At/above target',
};

export const TIER_ORDER: Record<PtTier, number> = {
    immediate: 0,
    gap50: 1,
    gap25: 2,
    gapSmall: 3,
    atTarget: 4,
};

/** Classify a supplier by how actionable its term position is. */
export function tierOf(s: PtSupplier): PtTier {
    if (s.currentTerm === 0 || (s.spend > 0 && s.immediateSpend >= s.spend * 0.25)) return 'immediate';
    const gapPct = s.gapDays / Math.max(1, s.targetTerm);
    if (gapPct >= 0.5) return 'gap50';
    if (gapPct >= 0.25) return 'gap25';
    if (s.gapDays > 0) return 'gapSmall';
    return 'atTarget';
}

/** Background / text colours for a tier pill (Mithra palette). */
export function tierColors(tier: PtTier): {bg: string; fg: string} {
    switch (tier) {
        case 'immediate':
            return {bg: 'rgba(255, 52, 53, 0.10)', fg: '#d32f2f'};
        case 'gap50':
            return {bg: 'rgba(250, 169, 21, 0.14)', fg: '#cc880c'};
        case 'gap25':
            return {bg: 'rgba(64, 136, 248, 0.12)', fg: '#2f6fd1'};
        case 'gapSmall':
            return {bg: 'rgba(68, 71, 78, 0.08)', fg: '#44474E'};
        case 'atTarget':
            return {bg: 'rgba(8, 165, 122, 0.12)', fg: '#08A57A'};
    }
}

/** Colour for a term-gap severity (how far below the policy target a supplier sits). */
export function gapColor(gapDays: number): string {
    if (gapDays >= 45) return '#FF3435';
    if (gapDays >= 20) return '#FAA915';
    if (gapDays > 0) return '#478FFA';
    return '#08A57A';
}

// ---------------------------------------------------------------------------
// Review workflow: disqualify suppliers where terms cannot be renegotiated
// (governments, banks, ...). Exclusions are kept locally and subtracted from
// the opportunity totals.
// ---------------------------------------------------------------------------

export type PtExclusions = Record<string, string>; // supplier id -> reason

export const EXCLUSION_REASONS = [
    'Government / public sector',
    'Bank / financial institution',
    'Intercompany',
    'Terms contractually fixed',
    'Other',
];

const EXCLUSIONS_STORAGE_KEY = 'pt-review-exclusions';

export function loadExclusions(): PtExclusions {
    try {
        return JSON.parse(localStorage.getItem(EXCLUSIONS_STORAGE_KEY) ?? '{}');
    } catch {
        return {};
    }
}

export function saveExclusions(exclusions: PtExclusions) {
    try {
        localStorage.setItem(EXCLUSIONS_STORAGE_KEY, JSON.stringify(exclusions));
    } catch {
        // Storage unavailable (private mode); exclusions stay session-only.
    }
}

// ---------------------------------------------------------------------------
// Term normalization lookup
// ---------------------------------------------------------------------------

export function buildTermLookup(mapping: PtTermMapping[]): Map<string, PtTermMapping> {
    return new Map(mapping.map(t => [t.term, t]));
}

/** Net-days label for a raw ERP payment-term text, e.g. "30d" or "EOM +2m". */
export function termDaysLabel(termText: string, lookup: Map<string, PtTermMapping>): string | null {
    const m = lookup.get(termText);
    if (!m || !m.mapped || m.gibberish) return null;
    if (m.rule.startsWith('eom') || m.eomMonths > 0) return `EOM +${m.eomMonths}m`;
    return `${m.days}d`;
}

/** Effective net days used for best-term comparison (EOM approximated). */
export function termEffectiveDays(termText: string, lookup: Map<string, PtTermMapping>): number | null {
    const m = lookup.get(termText);
    if (!m || !m.mapped || m.gibberish) return null;
    if (m.rule.startsWith('eom') || m.eomMonths > 0) return m.eomMonths * 30 + 15;
    return m.days;
}

export type PtRecommendation = {
    title: string;
    detail: string;
};

function recMoney(value: number, currency: string | null): string {
    if (currency) return formatCompactCurrency(value, currency);
    return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: value >= 1_000_000 ? 2 : 0,
    }).format(value);
}

/** Suggested negotiation play for a supplier, derived from its term profile. */
export function recommendActions(s: PtSupplier, targetTerm: number, currency: string | null = null): PtRecommendation[] {
    const recs: PtRecommendation[] = [];
    if (s.quickWinOpportunity > 0 && s.multipleTerms) {
        recs.push({
            title: `Quick win: align to the best existing term (${s.bestExistingTerm?.toFixed(0)} days)`,
            detail: `This supplier already accepts ${s.bestExistingTerm?.toFixed(0)}-day terms somewhere in the group. ` +
                `Aligning every entity to that term releases ${recMoney(s.quickWinOpportunity, currency)} ` +
                'with no new negotiation leverage needed.',
        });
    }
    if (s.immediateSpend > 0 && s.immediateSpend >= s.spend * 0.5) {
        recs.push({
            title: 'Move off immediate payment',
            detail: 'Most of this spend is paid immediately. Introduce a standard term — even 30 days ' +
                'releases the bulk of the opportunity without renegotiating pricing.',
        });
    }
    if (s.gapDays > 0) {
        recs.push({
            title: `Extend terms to ${Math.round(targetTerm)} days`,
            detail: `Current weighted term is ${s.currentTerm.toFixed(1)} days, ${s.gapDays.toFixed(1)} days ` +
                `short of policy. Align to the ${Math.round(targetTerm)}-day standard at the next renewal or volume commitment.`,
        });
    }
    if (s.multipleTerms) {
        recs.push({
            title: `Harmonise ${s.distinctTermCount} different terms`,
            detail: 'This supplier is paid on different terms across entities or business units. ' +
                'Consolidating to the single best term is usually accepted without resistance.',
        });
    }
    if (s.postedAfterDueSpend > 0 && s.postedAfterDueSpend >= s.spend * 0.25) {
        recs.push({
            title: 'Review invoice processing',
            detail: 'A significant share of invoices was posted after the estimated due date — an AP process ' +
                'risk that can trigger late-payment penalties and erode supplier goodwill.',
        });
    }
    if (s.discountAtRiskSpend > 0) {
        recs.push({
            title: 'Capture early-payment discounts',
            detail: 'Invoices were posted after the discount window closed. Faster processing would ' +
                'let treasury choose between the discount and the term.',
        });
    }
    if (recs.length === 0) {
        recs.push({
            title: 'Hold current terms',
            detail: 'This supplier already meets the policy target. Monitor for term erosion at renewal.',
        });
    }
    return recs;
}
